import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {useHistory} from 'react-router-dom';
import ReactTooltip from 'react-tooltip';
import ConfirmationDialog from '../../common/component/form/ConfirmationDialog';
import TextInput from '../../common/component/form/TextInput';
import Table from '../../common/component/table/Table';
import Role from '../../common/enums/Role';
import {formatDate} from '../../common/util/DateUtil';

import {sortCaseInsensitive} from '../../common/util/SortUtil';
import {resetOverviewFilters, setOverviewFilter} from '../action/trainingActions';

import trainingApi from '../api/trainingApi';
import {DateFilterTimespan} from '../enums/DateFilterTimespan';
import Disciplines from '../enums/Disciplines';
import '../style/TrainingOverview.less';
import {noEntryOptional, translateEnum} from '../util/TrainingUtilities';
import FilterPopup from './FilterPopup';

function TrainingOverview() {
    const [t] = useTranslation();
    const currentLanguage = useSelector(state => state.i18n.currentLanguage);
    const role = useSelector(state => state.account.role);
    const filter = useSelector(state => state.training.filter);

    const history = useHistory();
    const dispatch = useDispatch();

    const [deletionSelection, selectForDeletion] = useState(undefined);
    const [showDeletionConfirmation, setShowDeletionConfirmation] = useState(false);

    const dateRanges = [
        {
            label: t('Timespan.LAST_WEEK'),
            value: DateFilterTimespan.LAST_WEEK
        },
        {
            label: t('Timespan.LAST_MONTH'),
            value: DateFilterTimespan.LAST_MONTH
        }
    ];

    const [allTrainingStubs, setAllTrainingStubs] = useState([]);
    const [total, setTotal] = useState(0);

    const selectedDisciplines = filter.disciplines?.map(dis => {
        return {
            ...dis,
            label: t(`Disciplines.${dis}`)
        };
    });

    const disciplineOptions = Object.values(Disciplines).map(dis => {
        return {
            value: dis,
            label: t(`Disciplines.${dis}`)
        };
    });

    let tableSortBy = filter.sortBy;
    if (tableSortBy && tableSortBy.id === 'clubName') {
        tableSortBy = t('Training.CLUB');
    }

    function resetPageIndex() {
        dispatch(setOverviewFilter({
            type: 'page',
            value: 0
        }));
    }

    function loadTrainingStubs({
                                   pageIndex,
                                   sortBy,
                                   isDescending,
                                   triggerByFilterOrDelete = false,
                                   isReset = false
                               }) {
        const sortByValue = sortBy === t('Training.CLUB') ? 'clubName' : sortBy;

        let sortByToSend = filter.sortBy;

        if (triggerByFilterOrDelete) {
            dispatch(setOverviewFilter({
                type: 'page',
                value: 0
            }));
        } else {
            dispatch(setOverviewFilter({
                type: 'sortBy',
                value: {
                    id: sortByValue,
                    desc: isDescending
                }
            }));
            dispatch(setOverviewFilter({
                type: 'page',
                value: pageIndex
            }));

            sortByToSend = {
                id: sortByValue,
                desc: isDescending
            };
        }

        let filterToSend;

        if (isReset) {
            filterToSend = {
                disciplines: Object.values(Disciplines),
                page: 0,
                pageSize: 20
            };
        } else {
            filterToSend = {
                ...filter,
                disciplines: filter.disciplines.filter(dis => !!dis.show).map(dis => dis.value),
                page: pageIndex ? pageIndex : 0,
                sortBy: sortByToSend,
                language: currentLanguage
            };
        }

        trainingApi.search(
            filterToSend
        ).then(response => {
            if (response.data.success) {
                setAllTrainingStubs(response.data.result);
                setTotal(response.data.total);
            }
        });
    }

    function getPageCount() {
        return total ? Math.ceil(total / filter.pageSize) : 1;
    }

    function resetFilters() {
        dispatch(resetOverviewFilters());
        loadTrainingStubs({
            pageIndex: undefined,
            sortBy: undefined,
            isDescending: undefined,
            triggerByFilterOrDelete: true,
            isReset: true
        });
    }

    const columns =
        [{
            Header: t('Training.DATE'),
            accessor: 'date',
            width: '15%',
            disableFilters: true,
            Cell: props => {
                return formatDate(currentLanguage, props.cell.row.original.date);
            },
            sortType: (a, b) => new Date(a.original.date).getTime() >= new Date(b.original.date).getTime() ? 1 : -1
        }, {
            Header: t('Training.DISCIPLINE'),
            accessor: 'discipline',
            width: '16%',
            disableFilters: true,
            Cell: props => {
                const text = `${props.cell.row.original.discipline}`;
                return translateEnum(text);
            },
            sortType: (a, b) => sortCaseInsensitive(a.original.discipline, b.original.discipline)
        }, {
            Header: t('Training.LOCATION'),
            accessor: 'location',
            width: '25%',
            Cell: props => {
                const text = `${props.cell.row.original.location}`;
                return noEntryOptional(text);
            },
            sortType: (a, b) => sortCaseInsensitive(a.original.location, b.original.location)
        }, {
            Header:
                <span>
                    <button
                        data-tip={t('Button.FILTER')}
                        className="btn btn-primary ml-3"
                        onClick={() => loadTrainingStubs({
                            pageIndex: undefined,
                            sortBy: undefined,
                            isDescending: undefined,
                            triggerByFilterOrDelete: true
                        })}>
                        <FontAwesomeIcon icon="filter"/>
                    </button>
                    <button
                        data-tip={t('Button.FILTER_RESET')}
                        className="btn btn-primary ml-3"
                        onClick={resetFilters}>
                        <FontAwesomeIcon icon="eraser"/>
                    </button>
                </span>,
            accessor: 'trash',
            width: '19%',
            disableFilters: true,
            disableSortBy: true,
            Cell: props => {
                return (
                    <button
                        className="btn btn-transparent-secondary btn-icon"
                        onClick={(event) => {
                            event.stopPropagation();
                            selectForDeletion(props.cell.row.original.id);
                            setShowDeletionConfirmation(true);
                        }}
                    >
                        {<FontAwesomeIcon icon="trash"/>}
                    </button>
                );
            }
        }
        ];

    const deleteTraining = (selection) => {
        trainingApi.deleteTrainingById(selection).then((response) => {
            if (response.data.success) {
                loadTrainingStubs({
                    pageIndex: undefined,
                    sortBy: undefined,
                    isDescending: undefined,
                    triggerByFilterOrDelete: true
                });
            }
        }).catch((err) => {
            console.log('error deleting training', err);
        });
    };

    const selectTraining = (uuid) => {
        history.push(`/training/${uuid}`);
    };

    const handleChangeDisciplineFilter = (value) => {
        dispatch(setOverviewFilter({
            type: 'discipline',
            value
        }));
    };

    const buildDateRangeValues = () => {
        return dateRanges.map(dateRange => {
            const show = filter.date === dateRange.value;
            return {
                ...dateRange,
                show: show
            };
        });
    };

    const handleDateFilterChange = (value) => {
        let dateRange;
        if (value && value.value && value.id < dateRanges.length) {
            dateRange = dateRanges[value.id].value;
        } else {
            dateRange = null;
        }
        dispatch(setOverviewFilter({
            type: 'date',
            value: dateRange
        }));
    };

    // Only keep the column id in the store, not the translation
    const handleTextFilterChange = (row, value) => {
        dispatch(setOverviewFilter({
            type: row,
            value
        }));
    };

    const filterPopups = [<FilterPopup
                              options={dateRanges}
                              values={buildDateRangeValues()}
                              onChange={handleDateFilterChange}
                              isMulti={false}
                          />,
                          <FilterPopup options={disciplineOptions}
                                       values={[...selectedDisciplines]}
                                       onChange={(value) => handleChangeDisciplineFilter(value)}
                                       selectAllOptions={() => handleChangeDisciplineFilter('all')}
                                       deselectAllOptions={() => handleChangeDisciplineFilter('none')}
                          />
    ];

    const textFilter = [null, null,
                        <TextInput
                            value={filter.location}
                            onChange={(value) => handleTextFilterChange('location', value)}
                            placeholder={t('Table.SEARCH')}
                        />
    ];

    const isFilteredArray = [!!filter.date,
                             !!selectedDisciplines?.find(c => !c.show)
    ];

    if (role === Role.ADMINISTRATOR) {
        const clubColumn = {
            Header: t('Training.CLUB'),
            accessor: row => row.club ? row.club.name : row.club,
            width: '25%',
            Cell: props => {
                const text = `${props.cell.row.original.club && props.cell.row.original.club.name}`;
                return noEntryOptional(text);
            },
            sortType: (a, b) => sortCaseInsensitive(a.original.club && a.original.club.name, b.original.club && b.original.club.name)
        };
        columns.splice(columns.length - 1, 0, clubColumn);

        textFilter.push(
            <TextInput
                value={filter.clubName}
                onChange={(value) => handleTextFilterChange('clubName', value)}
                placeholder={t('Table.SEARCH')}
            />
        );
    }

    return (
        <div className="training-overview">
            <ReactTooltip type="dark" multiline={true}/>
            <div className="align-right">
                <h1>
                    {t('Training.HEADLINE_OVERVIEW')}
                </h1>
                <button
                    className="btn btn-primary ml-3"
                    onClick={() => {
                        history.push(`/training-edit/new`);
                    }}>
                    <FontAwesomeIcon icon="plus" className="mr-2"/>
                    {t('Button.IMPORT_TRAINING')}
                </button>
            </div>

            <div className="">
                <Table
                    data={allTrainingStubs}
                    columns={columns}
                    rowClick={selectTraining}
                    popup={filterPopups}
                    isFiltered={isFilteredArray}
                    sortBy="date"
                    filterable={false}
                    skipInternalFilter={true}
                    sortable={true}
                    filter={filter}
                    fetchData={loadTrainingStubs}
                    pageCount={getPageCount()}
                    textFilter={textFilter}
                    resetPageIndex={resetPageIndex}
                    sortFromOutside={[tableSortBy]}
                />
            </div>
            {
                showDeletionConfirmation &&
                <ConfirmationDialog
                    title={t('Training.DELETE_MODAL_TITLE')}
                    open={showDeletionConfirmation}
                    description={t('Training.DELETE_MODAL_QUESTION')}
                    accept={() => {
                        deleteTraining(deletionSelection);
                        selectForDeletion(undefined);
                        setShowDeletionConfirmation(false);

                    }}
                    cancel={() => {
                        selectForDeletion(undefined);
                        setShowDeletionConfirmation(false);
                    }}
                    acceptLabel={t('Button.DELETE')}
                />
            }
        </div>
    );
}

export default TrainingOverview;
